import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal, Type, effect, untracked } from '@angular/core';
import { IUserTag } from '@lb/api/mylb/interfaces/iuser-tag.interface';
import { MDDialogService, MDTagComponent, MDToolTipDirective } from '@melodic-ui/components';
import { UserTagDialogComponent } from '../user-tag-dialog/user-tag-dialog.component';
import { filter, take } from 'rxjs';
import { AdminPeopleService } from '@lb/api/mylb/services/admin-people.service';

@Component({
	selector: 'lb-user-tags',
	standalone: true,
	templateUrl: './user-tags.component.html',
	styleUrl: './user-tags.component.scss',
	imports: [CommonModule, MDTagComponent, MDToolTipDirective]
})
export class UserTagsComponent {
	private readonly adminPeopleService = inject(AdminPeopleService);
	private readonly _dialogService = inject(MDDialogService);

	userID = input.required<number>();
	userType = input.required<'INSTRUCTOR' | 'LEARNER' | 'CONTACT'>();

	maxWidth = input<string>('500px');
	showAddTagButton = input<boolean>(true);
	maxVisibleTags = input<number | null>(null);
	providedUserTags = input<IUserTag[] | null>(null);

	userTags = signal<IUserTag[]>([]);

	// Keep local signal in sync when provided tags change
	private readonly providedTagsEffect = effect(() => {
		const provided = this.providedUserTags();
		if (provided) {
			this.userTags.set(provided);
		}
	});

	visibleUserTags = computed(() => {
		const tags = this.userTags();
		const max = this.maxVisibleTags();
		if (max !== null) {
			return tags.slice(0, max);
		}

		return tags;
	});
	remainingUserTags = computed(() => {
		const tags = this.userTags();
		const max = this.maxVisibleTags();

		if (max === null) {
			return [];
		}

		return tags.slice(max);
	});
	remainingUserTagsCount = computed(() => this.remainingUserTags().length);

	userTagTooltip = computed(() =>
		this.remainingUserTags()
			.map((tag) => tag.tagTypeName)
			.join(', ')
	);

	// Reload from the service whenever the userID changes, but only when tags
	// aren't supplied via input. providedUserTags is read untracked so this
	// effect depends solely on userID — the provided list is kept in sync by
	// providedTagsEffect above.
	private readonly loadTagsEffect = effect(() => {
		const userID = this.userID();

		if (untracked(() => this.providedUserTags())) {
			return;
		}

		this.loadUserTags(userID);
	});

	loadUserTags(userID: number = this.userID()): void {
		this.adminPeopleService
			.getUserTags(userID)
			.pipe(take(1))
			.subscribe((tags) => this.userTags.set(tags));
	}

	openUserTagsDialog(tag: IUserTag | null = null): void {
		const userID = this.userID();

		// System tags (e.g. Under 13) are applied and removed by the daily sweep — not editable by users.
		if (!userID || tag?.isSystem) {
			return;
		}

		this._dialogService
			.open(UserTagDialogComponent as Type<any>, {
				userID: userID,
				userType: this.userType(),
				userTag: tag
			})
			.afterClosed.pipe(
				take(1),
				filter((x) => !!x)
			)
			.subscribe(() => {
				// Only reload from service if tags weren't provided externally
				if (!this.providedUserTags()) {
					this.loadUserTags();
				}
			});
	}
}
